import {
  useEffect,
  useMemo,
  useState,
  type MouseEvent,
} from "react";
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  Grid,
  Link as MuiLink,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import EmojiEventsOutlinedIcon from "@mui/icons-material/EmojiEventsOutlined";
import PointOfSaleOutlinedIcon from "@mui/icons-material/PointOfSaleOutlined";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import { Link } from "react-router-dom";
import moment from "moment";
import application from "../../core/application";
import { Shell } from "../../common/shell/shell.page";
import { PageHeader } from "../../common/components/page-header";
import { LoadingOverlay } from "../../common/components/loading-overlay";
import { Permission } from "../../users/user.entity";
import { sellRepository } from "../sell.repository";
import { userRepository } from "../../users/user.repository";
import { appSettingsRepository } from "../../settings/app-settings.repository";
import type { User } from "../../users/user.entity";
import { ReportPeriodSelector } from "../components/report-period-selector";
import {
  type ReportPeriodPreset,
  computePresetRange,
  formatPeriodHuman,
  parseCustomRange,
} from "../utils/report-period";
import { useModuleAccess } from "../../settings/module-access";
import { ModuleLockedView } from "../../settings/components/module-locked-view";

const money = (n: number) =>
  new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(n);

const SELLS_QUERY_LIMIT = 10000;

function effectivePercentForSeller(
  uid: string | null,
  userById: Map<string, User>,
  globalPct: number
): number {
  if (!uid) return globalPct;
  const u = userById.get(uid);
  if (!u) return globalPct;
  const c = u.commissionPercent;
  if (typeof c === "number" && !Number.isNaN(c)) return c;
  return globalPct;
}

type CommissionRow = {
  sellerKey: string;
  sellerName: string;
  rateLabel: string;
  sellCount: number;
  totalSold: number;
  commission: number;
};

/** Decoração: gráfico + moeda (SVG inline, sem dependência extra). */
function CommissionsHeroArt() {
  const theme = useTheme();
  const teal = theme.palette.primary.main;
  const tealLight = theme.palette.primary.light;
  return (
    <Box
      sx={{
        flexShrink: 0,
        width: { xs: "100%", sm: 200 },
        maxWidth: 220,
        opacity: 0.95,
      }}
      aria-hidden
    >
      <svg viewBox="0 0 220 130" width="100%" height="auto">
        <defs>
          <linearGradient id="ch-bar" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor={tealLight} stopOpacity={0.95} />
            <stop offset="100%" stopColor={teal} stopOpacity={0.85} />
          </linearGradient>
          <linearGradient id="ch-coin" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#fcd34d" />
            <stop offset="100%" stopColor="#f59e0b" />
          </linearGradient>
        </defs>
        <rect x="8" y="78" width="28" height="40" rx="4" fill="url(#ch-bar)" />
        <rect x="44" y="58" width="28" height="60" rx="4" fill="url(#ch-bar)" />
        <rect x="80" y="42" width="28" height="76" rx="4" fill="url(#ch-bar)" />
        <rect x="116" y="66" width="28" height="52" rx="4" fill="url(#ch-bar)" />
        <path
          d="M 152 28 L 188 28 Q 198 28 198 38 L 198 72 Q 198 82 188 82 L 152 82 Q 142 82 142 72 L 142 38 Q 142 28 152 28 Z"
          fill="url(#ch-coin)"
          opacity={0.95}
        />
        <text
          x="170"
          y="58"
          textAnchor="middle"
          fill="#78350f"
          fontSize="22"
          fontWeight={700}
          fontFamily="system-ui, sans-serif"
        >
          %
        </text>
        <path
          d="M 12 72 Q 50 48 90 62 T 170 52"
          fill="none"
          stroke={teal}
          strokeWidth="2.5"
          strokeLinecap="round"
          opacity={0.45}
        />
      </svg>
    </Box>
  );
}

export const CommissionsPage = () => {
  application.useHasPermission(Permission.ADMIN);
  const theme = useTheme();
  const moduleAccess = useModuleAccess();

  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState<CommissionRow[]>([]);
  const [commissionPercent, setCommissionPercent] = useState(0);
  const [totalSellsInWindow, setTotalSellsInWindow] = useState(0);
  const [hitQueryLimit, setHitQueryLimit] = useState(false);

  const [periodPreset, setPeriodPreset] =
    useState<ReportPeriodPreset>("current_month");
  const [customFrom, setCustomFrom] = useState(() =>
    moment().startOf("month").format("YYYY-MM-DD")
  );
  const [customTo, setCustomTo] = useState(() => moment().format("YYYY-MM-DD"));

  const dateRange = useMemo(() => {
    if (periodPreset === "custom") {
      return parseCustomRange(customFrom, customTo);
    }
    return computePresetRange(periodPreset);
  }, [periodPreset, customFrom, customTo]);

  const periodLabel = useMemo(
    () => formatPeriodHuman(dateRange.start, dateRange.end),
    [dateRange.start, dateRange.end]
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [sells, users, settings] = await Promise.all([
          sellRepository.getCompletedSellsInDateRange(
            dateRange.start,
            dateRange.end,
            SELLS_QUERY_LIMIT
          ),
          userRepository.getAll("name"),
          appSettingsRepository.getConfig(),
        ]);
        if (cancelled) return;

        setHitQueryLimit(sells.length >= SELLS_QUERY_LIMIT);
        const pct = settings.commissionPercent;
        setCommissionPercent(pct);
        setTotalSellsInWindow(sells.length);

        const userById = new Map(users.map((u) => [u.id, u] as const));

        const agg = new Map<
          string,
          { totalSold: number; sellCount: number; commission: number }
        >();
        for (const s of sells) {
          const uid = s.getSellerUserId();
          const key = uid ?? "__none__";
          const lineTotal = s.getSellTotal();
          const rateFraction =
            effectivePercentForSeller(uid, userById, pct) / 100;
          const cur = agg.get(key) ?? {
            totalSold: 0,
            sellCount: 0,
            commission: 0,
          };
          cur.totalSold += lineTotal;
          cur.sellCount += 1;
          cur.commission += lineTotal * rateFraction;
          agg.set(key, cur);
        }

        const list: CommissionRow[] = [];
        for (const [k, v] of Array.from(agg.entries())) {
          const sellerName =
            k === "__none__" ?
              "Sem vendedor (vendas antigas)" :
              (userById.get(k)?.name ?? `Usuário ${k.slice(0, 8)}…`);
          const applied = effectivePercentForSeller(
            k === "__none__" ? null : k,
            userById,
            pct
          );
          const isCustom =
            k !== "__none__" && userById.get(k)?.commissionPercent != null;
          const rateLabel =
            k === "__none__" ?
              `${pct}% · padrão` :
              `${applied}% · ${isCustom ? "personalizada" : "padrão"}`;
          list.push({
            sellerKey: k,
            sellerName,
            rateLabel,
            sellCount: v.sellCount,
            totalSold: v.totalSold,
            commission: v.commission,
          });
        }
        list.sort((a, b) => b.commission - a.commission);
        setRows(list);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [dateRange.start, dateRange.end]);

  const totals = useMemo(() => {
    return rows.reduce(
      (a, r) => ({
        sold: a.sold + r.totalSold,
        commission: a.commission + r.commission,
        sells: a.sells + r.sellCount,
      }),
      { sold: 0, commission: 0, sells: 0 }
    );
  }, [rows]);

  const handlePresetChange = (
    _e: MouseEvent<HTMLElement>,
    value: ReportPeriodPreset | null
  ) => {
    if (value === null) return;
    if (value === "custom" && periodPreset !== "custom") {
      const seed =
        periodPreset === "last_month" ||
        periodPreset === "current_month" ||
        periodPreset === "last_3_months" ?
          computePresetRange(periodPreset) :
          computePresetRange("current_month");
      setCustomFrom(moment(seed.start).format("YYYY-MM-DD"));
      setCustomTo(moment(seed.end).format("YYYY-MM-DD"));
    }
    if (value !== "custom") {
      const r = computePresetRange(value);
      setCustomFrom(moment(r.start).format("YYYY-MM-DD"));
      setCustomTo(moment(r.end).format("YYYY-MM-DD"));
    }
    setPeriodPreset(value);
  };

  if (moduleAccess.loading) {
    return (
      <Shell title="Comissões">
        <LoadingOverlay message="Carregando módulo…" subtle />
      </Shell>
    );
  }

  if (!moduleAccess.isEnabled("commissions")) {
    return (
      <Shell title="Comissões">
        <PageHeader title="Comissões" description="Acompanhamento de comissão por vendedor." />
        <ModuleLockedView
          moduleName="Comissões"
          groupId="commissions"
          details="Ative este módulo para visualizar e calcular comissões da equipe."
          features={["Relatório de comissão por vendedor", "Percentuais personalizados por usuário", "Histórico por período", "Taxa padrão global configurável"]}
        />
      </Shell>
    );
  }

  return (
    <Shell title="Comissões">
      {loading ? <LoadingOverlay message="Carregando vendas e comissões…" subtle /> : null}

      <PageHeader
        title="Comissões"
        description="Resumo por vendedor no período escolhido. Percentuais personalizados em Usuários; demais usam o padrão de Configurações."
      />

      <Stack spacing={3}>
        <Paper
          elevation={0}
          sx={{
            overflow: "hidden",
            borderRadius: 3,
            border: "1px solid",
            borderColor: alpha(theme.palette.primary.main, 0.22),
            background: `linear-gradient(135deg, ${alpha(
              theme.palette.primary.main,
              0.12
            )} 0%, ${alpha(theme.palette.primary.dark, 0.06)} 55%, ${alpha(
              theme.palette.background.paper,
              1
            )} 100%)`,
          }}
        >
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={2}
            alignItems={{ xs: "stretch", md: "center" }}
            justifyContent="space-between"
            sx={{ p: { xs: 2.5, md: 3 } }}
          >
            <Stack spacing={1.5} sx={{ flex: 1, minWidth: 0 }}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Box
                  sx={{
                    width: 44,
                    height: 44,
                    borderRadius: 2,
                    bgcolor: alpha(theme.palette.primary.main, 0.15),
                    color: "primary.main",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <EmojiEventsOutlinedIcon />
                </Box>
                <Box>
                  <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 0.08 }}>
                    Painel de performance
                  </Typography>
                  <Typography variant="h5" component="p" sx={{ fontWeight: 800, letterSpacing: "-0.03em" }}>
                    Comissões por vendedor
                  </Typography>
                </Box>
              </Stack>
              <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
                <Chip
                  icon={<CalendarMonthOutlinedIcon sx={{ fontSize: 18 }} />}
                  label={periodLabel}
                  size="small"
                  sx={{
                    fontWeight: 600,
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                  }}
                />
                <Chip
                  label={`Padrão global ${commissionPercent}%`}
                  size="small"
                  variant="outlined"
                  sx={{ fontWeight: 500 }}
                />
              </Stack>
            </Stack>
            <CommissionsHeroArt />
          </Stack>
        </Paper>

        <Card elevation={0}>
          <CardContent sx={{ p: { xs: 2, sm: 2.25 } }}>
            <ReportPeriodSelector
              value={periodPreset}
              onChange={handlePresetChange}
              customFrom={customFrom}
              customTo={customTo}
              onCustomFromChange={setCustomFrom}
              onCustomToChange={setCustomTo}
            />
          </CardContent>
        </Card>

        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <Card
              elevation={0}
              sx={{
                height: "100%",
                background: `linear-gradient(145deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(
                  theme.palette.background.paper,
                  1
                )} 60%)`,
              }}
            >
              <CardContent sx={{ p: 2.5 }}>
                <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
                  <Box>
                    <Typography variant="caption" color="text.secondary" fontWeight={600}>
                      Total vendido
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: 800, mt: 0.5, letterSpacing: "-0.02em" }}>
                      {money(totals.sold)}
                    </Typography>
                  </Box>
                  <TrendingUpIcon sx={{ color: "primary.main", opacity: 0.85 }} />
                </Stack>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Card
              elevation={0}
              sx={{
                height: "100%",
                background: `linear-gradient(145deg, ${alpha("#f59e0b", 0.12)} 0%, ${alpha(
                  theme.palette.background.paper,
                  1
                )} 60%)`,
              }}
            >
              <CardContent sx={{ p: 2.5 }}>
                <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
                  <Box>
                    <Typography variant="caption" color="text.secondary" fontWeight={600}>
                      Comissão estimada
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: 800, mt: 0.5, letterSpacing: "-0.02em" }}>
                      {money(totals.commission)}
                    </Typography>
                  </Box>
                  <AccountBalanceWalletOutlinedIcon sx={{ color: "warning.main", opacity: 0.9 }} />
                </Stack>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Card
              elevation={0}
              sx={{
                height: "100%",
                background: `linear-gradient(145deg, ${alpha(theme.palette.secondary.main, 0.1)} 0%, ${alpha(
                  theme.palette.background.paper,
                  1
                )} 60%)`,
              }}
            >
              <CardContent sx={{ p: 2.5 }}>
                <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
                  <Box>
                    <Typography variant="caption" color="text.secondary" fontWeight={600}>
                      Vendas no período
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: 800, mt: 0.5, letterSpacing: "-0.02em" }}>
                      {totals.sells}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
                      {totalSellsInWindow} carregadas
                    </Typography>
                  </Box>
                  <PointOfSaleOutlinedIcon sx={{ color: "secondary.main", opacity: 0.9 }} />
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Alert severity="info" variant="outlined" sx={{ borderRadius: 2 }}>
          Padrão global: <strong>{commissionPercent}%</strong>. Comissão por usuário em{" "}
          <MuiLink component={Link} to="/usuarios" fontWeight={600}>
            Usuários
          </MuiLink>
          {" · "}
          <MuiLink component={Link} to="/configuracoes" fontWeight={600}>
            Configurações
          </MuiLink>
          .
        </Alert>

        {hitQueryLimit ? (
          <Alert severity="warning" variant="outlined" sx={{ borderRadius: 2 }}>
            Foram encontradas pelo menos {SELLS_QUERY_LIMIT.toLocaleString("pt-BR")} vendas neste intervalo.
            Os totais podem estar truncados; reduza o período ou aumente o limite no código, se necessário.
          </Alert>
        ) : null}

        <Card elevation={0}>
          <CardContent sx={{ p: 0 }}>
            <Box sx={{ px: 2.5, py: 2, borderBottom: 1, borderColor: "divider" }}>
              <Typography variant="subtitle1" fontWeight={700}>
                Detalhe por vendedor
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Ordenado pela comissão estimada (maior primeiro).
              </Typography>
            </Box>
            <TableContainer>
              <Table size="medium" aria-label="Comissões por vendedor">
                <TableHead>
                  <TableRow
                    sx={{
                      bgcolor: alpha(theme.palette.primary.main, 0.06),
                      "& th": { fontWeight: 700, fontSize: "0.8125rem", py: 1.5 },
                    }}
                  >
                    <TableCell>Vendedor</TableCell>
                    <TableCell>Taxa aplicada</TableCell>
                    <TableCell align="right">Vendas</TableCell>
                    <TableCell align="right">Total vendido</TableCell>
                    <TableCell align="right">Comissão</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.length === 0 && !loading ? (
                    <TableRow>
                      <TableCell colSpan={5}>
                        <Box sx={{ py: 5, textAlign: "center" }}>
                          <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
                            Nenhuma venda concluída neste período.
                          </Typography>
                          <Typography variant="body2" color="text.disabled">
                            Ajuste o filtro de datas ou aguarde novas vendas finalizadas no caixa.
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ) : (
                    rows.map((r) => (
                      <TableRow
                        key={r.sellerKey}
                        hover
                        sx={{ "&:last-of-type td": { borderBottom: 0 } }}
                      >
                        <TableCell sx={{ fontWeight: 600 }}>{r.sellerName}</TableCell>
                        <TableCell>
                          <Chip
                            label={r.rateLabel}
                            size="small"
                            variant="outlined"
                            sx={{ fontWeight: 500, borderRadius: 2 }}
                          />
                        </TableCell>
                        <TableCell align="right">{r.sellCount}</TableCell>
                        <TableCell align="right">{money(r.totalSold)}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700, color: "primary.dark" }}>
                          {money(r.commission)}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                  {rows.length > 0 ? (
                    <TableRow
                      sx={{
                        bgcolor: alpha(theme.palette.primary.main, 0.08),
                        "& td": { fontWeight: 700, borderTop: `2px solid ${alpha(theme.palette.primary.main, 0.2)}` },
                      }}
                    >
                      <TableCell>Total</TableCell>
                      <TableCell />
                      <TableCell align="right">{totals.sells}</TableCell>
                      <TableCell align="right">{money(totals.sold)}</TableCell>
                      <TableCell align="right" sx={{ color: "primary.dark" }}>
                        {money(totals.commission)}
                      </TableCell>
                    </TableRow>
                  ) : null}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>

        <Box sx={{ pb: 2 }} />
      </Stack>
    </Shell>
  );
};
